import { saveCallbackStatus } from '@finpay-development/shared-components';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Payor } from '@finpay-estimation/estimation-types/dist/types/vob-types';

import {
    emptyVob,
    FacilityPayers,
    Vob,
    vobClassificationsItem,
} from '../models/vob';
import {
    getEstimatorFacilityPayers,
    getVob,
    getVOBForPatientRecords,
    getVobClassifications,
    saveVob,
    saveVOBForFinpassPatientRecords, 
    postVob,
} from './vob-thunk';
import { createSelectedVob, saveSelectedVob } from '../../shared/state/vob-thunk';

interface vobSliceShape {
  vob: Vob,
  vobClassifications: vobClassificationsItem[],
  facilityPayers: FacilityPayers[],
  isLoading: {
    vobLoadingStatus: saveCallbackStatus,
    isLoadingVob: boolean,
    isLoadingGetVob: boolean,
    isLoadingVobClassifications: boolean,
    vobClassificationsLoadingStatus: saveCallbackStatus,
    isLoadingFacilityLocPayers: boolean,
  }
  isError: boolean,
  urlParams?: any
}

const initialState: vobSliceShape = {
    vob: emptyVob,
    vobClassifications: [],
    facilityPayers: [],
    isLoading: {
        vobLoadingStatus: saveCallbackStatus.none,
        isLoadingVob: false,
        isLoadingGetVob: false,
        isLoadingVobClassifications: false,
        vobClassificationsLoadingStatus: saveCallbackStatus.none,
        isLoadingFacilityLocPayers: false,
    },
    isError: false,
    urlParams: undefined
};

export const vobSlice = createSlice({
	name: "vobContext",
	initialState,
	reducers: {
		setVOB: (state, action) => {
			state.vob = {
        ...state.vob,
        ...action.payload};
		},
		clearVOB: (state) => {
			state.vob = initialState.vob;
		},
		clearFacilityPayers: (state) => {
			state.facilityPayers = initialState.facilityPayers;
		},
		clearVobClassificationsLoadingStatus: (state) => {
			state.isLoading.vobClassificationsLoadingStatus = initialState.isLoading.vobClassificationsLoadingStatus;
		},
    clearPaymentURLParams: (state) => {
        if (state.vob.urlParams && state.vob.urlParams.hasOwnProperty('op')) {
           delete state.vob.urlParams.op;
        }
	},
        initializePatientVOBState(state){
            state.vob = emptyVob
        },
        setVobAndEstimate: (state, action) => {
            state.vob = action.payload.vob;
        },
	},
  extraReducers: (builder) => {
    builder.addCase(saveVob.pending, (state, action) => {
      state.isLoading.isLoadingGetVob = true;
    });
    builder.addCase(saveVob.fulfilled, (state, action: PayloadAction<any>) => {
      state.isLoading.isLoadingGetVob = false;
      state.vob = action.payload.vobBody;
      state.vob.fpClientId = action.payload.fpClientId
      state.vob.vobId = action.payload.vobId;
    });
    builder.addCase(saveVob.rejected, (state, action) => {
      state.isLoading.isLoadingGetVob = false;
      state.isError = true;
    });
    builder.addCase(createSelectedVob.pending, (state, action) => {
      state.isLoading.isLoadingGetVob = true;
    });
    builder.addCase(createSelectedVob.fulfilled, (state, action: PayloadAction<any>) => {
      state.isLoading.isLoadingGetVob = false;
      state.vob = action.payload;
    });
    builder.addCase(createSelectedVob.rejected, (state, action) => {
      state.isLoading.isLoadingGetVob = false;
      state.isError = true;
    });
    builder.addCase(saveSelectedVob.pending, (state, action) => {
      state.isLoading.isLoadingGetVob = true;
    });
    builder.addCase(saveSelectedVob.fulfilled, (state, action: PayloadAction<any>) => {
      state.isLoading.isLoadingGetVob = false;
      state.vob = action.payload;
    });
    builder.addCase(saveSelectedVob.rejected, (state, action) => {
      state.isLoading.isLoadingGetVob = false;
      state.isError = true;
    });
    builder.addCase(getVob.pending, (state, action) => {
      state.isLoading.isLoadingVob = true;
    });
    builder.addCase(getVob.fulfilled, (state, action: PayloadAction<any>) => {
      state.isLoading.isLoadingVob = false;
      state.vob = action.payload.vobBody;
      state.vob.vobId = action.payload.vobId;
    });
    builder.addCase(getVob.rejected, (state, action) => {
      state.isLoading.isLoadingVob = false;
      state.isError = true;
    });
    builder.addCase(getVobClassifications.pending, (state, action) => {
      state.isLoading.isLoadingVobClassifications = true;
      state.isLoading.vobClassificationsLoadingStatus = saveCallbackStatus.none;
    });
    builder.addCase(getVobClassifications.fulfilled, (state, action) => {
      state.isLoading.isLoadingVobClassifications = false;
      state.isLoading.vobClassificationsLoadingStatus = saveCallbackStatus.success;
      state.vobClassifications = action.payload;
    });
    builder.addCase(getVobClassifications.rejected, (state, action) => {
      state.isLoading.isLoadingVobClassifications = false;
      state.isLoading.vobClassificationsLoadingStatus = saveCallbackStatus.error;
      state.isError = true;
    });
    builder.addCase(getEstimatorFacilityPayers.pending, (state, action) => {
      state.isLoading.isLoadingFacilityLocPayers = true;
    });
    builder.addCase(getEstimatorFacilityPayers.fulfilled, (state, action) => {
      state.isLoading.isLoadingFacilityLocPayers = false;
      state.facilityPayers = [...action.payload!].sort((a, b) => a.facilityPayorName! > b.facilityPayorName! ? 1 : -1)
    });
    builder.addCase(getEstimatorFacilityPayers.rejected, (state, action) => {
      state.isLoading.isLoadingFacilityLocPayers = false;
      state.isError = true;
    });
    builder.addCase(getVOBForPatientRecords.fulfilled, (state, action: PayloadAction<any>) => {
      state.isLoading.isLoadingVob = false;
      state.vob = action.payload.vobBody;
      state.vob.vobId = action.payload.vobId;
    });
    builder.addCase(saveVOBForFinpassPatientRecords.fulfilled, (state, action) => {
      state.vob = action.payload.vobBody;
      state.vob.vobId = action.payload.vobId;
    })
    builder.addCase(postVob.fulfilled, (state, action) => {
      state.vob = action.payload.vobBody;
      state.vob.vobId = action.payload.vobId;
    })
  },
});

export const { setVOB, clearVOB, clearFacilityPayers, clearVobClassificationsLoadingStatus, clearPaymentURLParams, initializePatientVOBState,setVobAndEstimate } = vobSlice.actions;

export default vobSlice.reducer;
